import type {
  ActivityEntry,
  CalendarFeed,
  CalendarFeedInput,
  CastingInput,
  CastingRow,
  ClientDetail,
  ClientInput,
  ClientWithStats,
  CurrentUser,
  DashboardSummary,
  DeliverableDetail,
  DeliverableInput,
  DeliverableRow,
  Document,
  FrameioProjectSummary,
  FrameioSyncStatus,
  IntakeBatch,
  IntakeBatchDetail,
  IntakePreview,
  IntakeRow,
  Language,
  LanguageVersionInput,
  LanguageVersionRow,
  Market,
  OutstandingItem,
  Paginated,
  Project,
  ProjectDetail,
  ProjectFrameioComments,
  ProjectInput,
  ProjectMember,
  ProjectWithStats,
  InviteUserInput,
  Review,
  ReviewInput,
  ScheduleItemInput,
  ScheduleItemRow,
  UpdateUserRoleInput,
  UserDirectory,
  Vendor,
} from '@bedouin/shared'
import { localToday } from '@/lib/format'
import { api, type QueryValue } from './client'

type Query = Record<string, QueryValue>

/**
 * One typed function per endpoint. Views and stores call these rather than
 * assembling URLs, so a route change lands in exactly one place.
 */
export const resources = {
  me: (signal?: AbortSignal) => api.get<CurrentUser>('/api/me', undefined, signal),

  // Overdue is decided on the server, so it is told what day it is here.
  dashboard: {
    summary: (query?: Query, signal?: AbortSignal) =>
      api.get<DashboardSummary>('/api/dashboard/summary', { today: localToday(), ...query }, signal),
    outstanding: (query?: Query, signal?: AbortSignal) =>
      api.get<OutstandingItem[]>('/api/dashboard/outstanding', { today: localToday(), ...query }, signal),
    calendar: (query?: Query, signal?: AbortSignal) =>
      api.get<OutstandingItem[]>('/api/dashboard/calendar', { today: localToday(), ...query }, signal),
  },

  clients: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<ClientWithStats[]>('/api/clients', query, signal),
    get: (id: string, signal?: AbortSignal) =>
      api.get<ClientDetail>(`/api/clients/${id}`, undefined, signal),
    create: (body: ClientInput) => api.post<ClientWithStats>('/api/clients', body),
    update: (id: string, body: Partial<ClientInput>) =>
      api.patch<ClientWithStats>(`/api/clients/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/clients/${id}`),
  },

  projects: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<ProjectWithStats[]>('/api/projects', query, signal),
    get: (id: string, signal?: AbortSignal) =>
      api.get<ProjectDetail>(`/api/projects/${id}`, undefined, signal),
    create: (body: ProjectInput) => api.post<Project>('/api/projects', body),
    update: (id: string, body: Partial<ProjectInput>) =>
      api.patch<Project>(`/api/projects/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/projects/${id}`),
    members: (id: string) => api.get<ProjectMember[]>(`/api/projects/${id}/members`),
    addMember: (id: string, body: Record<string, unknown>) =>
      api.post<ProjectMember>(`/api/projects/${id}/members`, body),
    /** Editors only: client users are refused. */
    activity: (id: string, limit = 20, signal?: AbortSignal) =>
      api.get<ActivityEntry[]>(`/api/projects/${id}/activity`, { limit: String(limit) }, signal),
    updateMember: (id: string, memberId: string, body: Record<string, unknown>) =>
      api.patch<ProjectMember>(`/api/projects/${id}/members/${memberId}`, body),
    removeMember: (id: string, memberId: string) =>
      api.delete<{ id: string }>(`/api/projects/${id}/members/${memberId}`),
    frameioComments: (id: string, signal?: AbortSignal) =>
      api.get<ProjectFrameioComments>(`/api/projects/${id}/frameio-comments`, undefined, signal),
    frameioSync: (id: string, restart = false) =>
      api.post<FrameioSyncStatus | null>(`/api/projects/${id}/frameio-sync`, { restart }),
  },

  deliverables: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<Paginated<DeliverableRow>>('/api/deliverables', query, signal),
    get: (id: string, signal?: AbortSignal) =>
      api.get<DeliverableDetail>(`/api/deliverables/${id}`, undefined, signal),
    create: (body: DeliverableInput & { language_codes?: string[] }) =>
      api.post<DeliverableRow>('/api/deliverables', body),
    update: (id: string, body: Partial<DeliverableInput>) =>
      api.patch<DeliverableRow>(`/api/deliverables/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/deliverables/${id}`),
    bulkStatus: (ids: string[], status: string) =>
      api.post<{ updated: number }>('/api/deliverables/bulk-status', { ids, status }),
  },

  languageVersions: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<Paginated<LanguageVersionRow>>('/api/language-versions', query, signal),
    get: (id: string) => api.get<LanguageVersionRow>(`/api/language-versions/${id}`),
    create: (body: LanguageVersionInput) =>
      api.post<LanguageVersionRow>('/api/language-versions', body),
    update: (id: string, body: Partial<LanguageVersionInput>) =>
      api.patch<LanguageVersionRow>(`/api/language-versions/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/language-versions/${id}`),
    bulkAdd: (deliverableId: string, languageCodes: string[], extra?: Record<string, unknown>) =>
      api.post<LanguageVersionRow[]>('/api/language-versions/bulk', {
        deliverable_id: deliverableId,
        language_codes: languageCodes,
        ...extra,
      }),
  },

  casting: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<Paginated<CastingRow>>('/api/casting', query, signal),
    get: (id: string) => api.get<CastingRow>(`/api/casting/${id}`),
    create: (body: CastingInput) => api.post<CastingRow>('/api/casting', body),
    update: (id: string, body: Partial<CastingInput>) =>
      api.patch<CastingRow>(`/api/casting/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/casting/${id}`),
  },

  vendors: {
    list: (query?: Query, signal?: AbortSignal) => api.get<Vendor[]>('/api/vendors', query, signal),
    create: (body: Record<string, unknown>) => api.post<Vendor>('/api/vendors', body),
    update: (id: string, body: Record<string, unknown>) =>
      api.patch<Vendor>(`/api/vendors/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/vendors/${id}`),
  },

  markets: {
    list: (query?: Query, signal?: AbortSignal) => api.get<Market[]>('/api/markets', query, signal),
    create: (body: Record<string, unknown>) => api.post<Market>('/api/markets', body),
    update: (id: string, body: Record<string, unknown>) =>
      api.patch<Market>(`/api/markets/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/markets/${id}`),
  },

  languages: {
    list: (query?: Query) => api.get<Language[]>('/api/languages', query),
    create: (body: { code: string; name: string; native_name?: string | null }) =>
      api.post<Language>('/api/languages', body),
  },

  documents: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<Document[]>('/api/documents', query, signal),
    createLink: (body: Record<string, unknown>) => api.post<Document>('/api/documents', body),
    upload: (form: FormData) => api.upload<Document>('/api/documents/upload', form),
    downloadUrl: (id: string) => api.url(`/api/documents/${id}/download`),
    remove: (id: string) => api.delete<{ id: string }>(`/api/documents/${id}`),
  },

  schedule: {
    list: (projectId: string, signal?: AbortSignal) =>
      api.get<ScheduleItemRow[]>('/api/schedule-items', { project_id: projectId }, signal),
    create: (body: ScheduleItemInput) => api.post<ScheduleItemRow>('/api/schedule-items', body),
    bulk: (projectId: string, items: Omit<ScheduleItemInput, 'project_id'>[]) =>
      api.post<{ created: number; ids: string[] }>('/api/schedule-items/bulk', {
        project_id: projectId,
        items,
      }),
    update: (id: string, body: Partial<Omit<ScheduleItemInput, 'project_id'>>) =>
      api.patch<ScheduleItemRow>(`/api/schedule-items/${id}`, body),
    remove: (id: string) =>
      api.delete<{ id: string; deleted: boolean; milestones_detached: number }>(
        `/api/schedule-items/${id}`,
      ),
  },
  calendarFeeds: {
    list: (projectId?: string, signal?: AbortSignal) =>
      api.get<CalendarFeed[]>(
        '/api/calendar-feeds',
        projectId ? { project_id: projectId } : undefined,
        signal,
      ),
    save: (body: CalendarFeedInput) => api.post<CalendarFeed>('/api/calendar-feeds', body),
    reset: (id: string) => api.post<CalendarFeed>(`/api/calendar-feeds/${id}/reset`),
    remove: (id: string) => api.delete<{ id: string; deleted: boolean }>(`/api/calendar-feeds/${id}`),
  },
  reviews: {
    list: (query?: Query, signal?: AbortSignal) => api.get<Review[]>('/api/reviews', query, signal),
    create: (body: ReviewInput) => api.post<Review>('/api/reviews', body),
    update: (id: string, body: Partial<ReviewInput>) => api.patch<Review>(`/api/reviews/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/reviews/${id}`),
  },

  users: {
    directory: (signal?: AbortSignal) =>
      api.get<UserDirectory>('/api/users', undefined, signal),
    invite: (body: InviteUserInput) =>
      api.post<{ id: string; email: string }>('/api/users/invite', body),
    setRole: (id: string, body: UpdateUserRoleInput) =>
      api.patch<{ id: string; role: string }>(`/api/users/${id}`, body),
    revokeInvitation: (id: string) =>
      api.delete<{ id: string }>(`/api/users/invitations/${id}`),
  },

  integrations: {
    frameioStatus: (signal?: AbortSignal) =>
      api.get<{
        configured: boolean
        connected: boolean
        connected_as: string | null
        account_id: string | null
        workspace_id: string | null
        expires_at: string | null
        last_error: string | null
        last_synced_at: string | null
        webhook_secret_set: boolean
        live_updates: boolean
        webhook_url: string | null
        webhook_created_at: string | null
        webhook_last_event_at: string | null
        callback_url: string | null
        scopes_requested: string
        api_base: string
        wrong_client_id_type: boolean
      }>('/api/integrations/frameio/status', undefined, signal),
    frameioConnect: () =>
      api.post<{ authorize_url: string }>('/api/integrations/frameio/connect'),
    frameioComplete: (code: string, state: string) =>
      api.post<{ connected: boolean; connected_as: string | null; expires_at: string | null }>(
        '/api/integrations/frameio/callback',
        { code, state },
      ),
    frameioProjects: (signal?: AbortSignal) =>
      api.get<FrameioProjectSummary[]>('/api/integrations/frameio/projects', undefined, signal),
    frameioProject: (id: string, signal?: AbortSignal) =>
      api.get<FrameioProjectSummary | null>(
        `/api/integrations/frameio/projects/${id}`,
        undefined,
        signal,
      ),
    frameioWebhookEnable: () =>
      api.post<{ live_updates: boolean; webhook_url: string }>('/api/integrations/frameio/webhook'),
    frameioWebhookDisable: () =>
      api.delete<{ live_updates: boolean }>('/api/integrations/frameio/webhook'),
    frameioDisconnect: () => api.delete<{ disconnected: boolean }>('/api/integrations/frameio'),
  },

  intake: {
    preview: (headers: string[], rows?: Record<string, unknown>[]) =>
      api.post<IntakePreview>('/api/intake/preview', { headers, rows }),
    batches: (query?: Query, signal?: AbortSignal) =>
      api.get<IntakeBatch[]>('/api/intake/batches', query, signal),
    batch: (id: string, signal?: AbortSignal) =>
      api.get<IntakeBatchDetail>(`/api/intake/batches/${id}`, undefined, signal),
    createBatch: (body: Record<string, unknown>) =>
      api.post<IntakeBatch>('/api/intake/batches', body),
    remap: (id: string, columnMap: Record<string, string>) =>
      api.post<{ remapped: number }>(`/api/intake/batches/${id}/remap`, { column_map: columnMap }),
    /** Approve every row still pending or held in a batch, in one request. */
    approveAll: (id: string) => api.post<{ approved: number }>(`/api/intake/batches/${id}/approve`, {}),
    /** Settle what market or language text in the file means; only those are re-read. */
    answer: (id: string, answers: { market_map?: Record<string, string>; language_map?: Record<string, string> }) =>
      api.post<{ remapped: number }>(`/api/intake/batches/${id}/remap`, answers),
    updateRow: (id: string, body: Record<string, unknown>) =>
      api.patch<IntakeRow>(`/api/intake/rows/${id}`, body),
    import: (id: string, includePending = false) =>
      api.post<{
        imported: number
        deliverable_ids: string[]
        needs_attention: number
        unmatched_references: string[]
      }>(`/api/intake/batches/${id}/import`, { include_pending: includePending }),
    removeBatch: (id: string) => api.delete<{ id: string }>(`/api/intake/batches/${id}`),
  },
}
